import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, createFileRoute, useNavigate } from '@tanstack/react-router'

import { LogoOP } from '@/components/marca'
import { EsqueletoLinhas } from '@/components/ui/estado'
import { SemConexao } from '@/components/posto/sem-conexao'
import { VincularTablet } from '@/components/posto/vincular-tablet'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/posto/')({ component: EntradaDoPosto })

/**
 * Porta de entrada do tablet (ADR 0010). Tablet vinculado abre direto no seu
 * posto; sem vínculo, mostra "Vincular este tablet". Quem tem login de gestão
 * vê a lista de postos e, abrindo um, pode vincular o próprio navegador a ele.
 */
function EntradaDoPosto() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const dispositivo = useQuery({ ...trpc.dispositivos.meu.queryOptions(), retry: 1 })

  if (dispositivo.data) {
    return <Navigate to="/posto/$postoId" params={{ postoId: dispositivo.data.posto.id }} replace />
  }
  if (dispositivo.isError) {
    return (
      <main className="flex min-h-full flex-col">
        <SemConexao
          onTentarDeNovo={() => void dispositivo.refetch()}
          tentando={dispositivo.isFetching}
        />
      </main>
    )
  }
  if (dispositivo.data === undefined) {
    return (
      <main className="grid min-h-full place-items-center p-8">
        <EsqueletoLinhas linhas={3} className="w-full max-w-md" rotulo="Identificando o tablet" />
      </main>
    )
  }

  return (
    <main className="flex min-h-full flex-col">
      <div className="px-6 pt-6">
        <LogoOP tamanho="size-9" />
      </div>
      <VincularTablet
        semMarca
        onVinculado={(posto) =>
          void navigate({ to: '/posto/$postoId', params: { postoId: posto.id }, replace: true })
        }
      />
      <ListaParaGestao />
    </main>
  )
}

function ListaParaGestao() {
  const trpc = useTRPC()
  const estrutura = useQuery({ ...trpc.postos.estrutura.queryOptions(), retry: false })

  if (estrutura.isError) {
    return (
      <p className="p-6 text-center text-sm text-fg-muted">
        É da gestão?{' '}
        <Link to="/login" search={{ redirect: '/posto' }} className="text-brand underline">
          Entre
        </Link>{' '}
        para ver a lista de postos.
      </p>
    )
  }
  if (!estrutura.data) return null

  return (
    <section className="mx-auto w-full max-w-4xl p-6">
      <h2 className="mb-1 text-xl font-bold">Postos cadastrados</h2>
      <p className="mb-6 text-sm text-fg-muted">
        Abra um posto para vincular este navegador a ele (sem código, por estar na gestão).
      </p>
      <div className="flex flex-col gap-8">
        {estrutura.data.map((linha) => (
          <section key={linha.id}>
            <h3 className="mb-3 text-sm font-semibold tracking-widest text-fg-muted uppercase">
              {linha.nome}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {linha.setores.flatMap((setor) =>
                setor.postos.map((posto) => (
                  <Link
                    key={posto.id}
                    to="/posto/$postoId"
                    params={{ postoId: posto.id }}
                    className="flex h-tap press items-center justify-between card px-5 hover:border-brand/60"
                  >
                    <span className="text-lg font-semibold">{posto.nome}</span>
                    <span className="font-mono text-sm text-fg-subtle">{posto.codigo}</span>
                  </Link>
                )),
              )}
            </div>
          </section>
        ))}
      </div>
    </section>
  )
}
